import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { BankAccountSummary } from '@/types/accounts';
import { ConsentRenewalDialog, type ConsentRenewalTarget } from '@/components/cash/ConsentRefreshDialog';

type ConsentRequiredError = ConsentRenewalTarget;

interface BankPageContextType {
  consentRequiredError: ConsentRequiredError | null;
  setConsentRequiredError: (error: ConsentRequiredError | null) => void;
  handleConsentRefreshSuccess: (accounts: BankAccountSummary[]) => void;
}

const BankPageContext = createContext<BankPageContextType | undefined>(undefined);

export function useBankPageContext() {
  const context = useContext(BankPageContext);
  if (context === undefined) {
    throw new Error('useBankPageContext must be used within a BankPageProvider');
  }
  return context;
}

// Optional version that doesn't throw an error
export function useBankPageContextOptional() {
  const context = useContext(BankPageContext);
  return context; // Returns undefined if not in provider
}

interface BankPageProviderProps {
  children: React.ReactNode;
  onAccountsRefresh?: (accounts: BankAccountSummary[]) => void;
}

export function BankPageProvider({ children, onAccountsRefresh }: BankPageProviderProps) {
  const [consentRequiredError, setConsentRequiredError] = useState<ConsentRequiredError | null>(null);

  const handleConsentRefreshSuccess = useCallback((updatedAccounts: BankAccountSummary[]) => {
    console.log("BankPageProvider: Consent refresh successful, clearing error and updating accounts:", updatedAccounts);
    setConsentRequiredError(null);
    onAccountsRefresh?.(updatedAccounts);
  }, [onAccountsRefresh]);

  const clearConsentError = useCallback(() => {
    console.log("BankPageProvider: Clearing consent error");
    setConsentRequiredError(null);
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    consentRequiredError,
    setConsentRequiredError,
    handleConsentRefreshSuccess
  }), [consentRequiredError, setConsentRequiredError, handleConsentRefreshSuccess]);

  console.log("🏦 BankPageProvider render - consentRequiredError:", consentRequiredError);

  return (
    <BankPageContext.Provider value={contextValue}>
      {children}
      
      {/* Consent Refresh Dialog - managed at provider level */}
      {consentRequiredError && (
        <>
          {console.log("🚀 BankPageProvider: Rendering ConsentRefreshDialog with:", consentRequiredError)}
          <ConsentRenewalDialog target={consentRequiredError} onClose={clearConsentError} onSuccess={handleConsentRefreshSuccess} />
        </>
      )}
    </BankPageContext.Provider>
  );
}